import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { AdminSidebarComponent } from '../sidebar/admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent, withCellTemplates } from '../../shared/components/primeng';
import { FilterPanelComponent, FilterField } from '../../shared/components/filter-panel/filter-panel.component';
import { DataProviderEntityFormComponent, DataProviderEntityFormData } from '../../shared/components/data-provider-entity-form/data-provider-entity-form.component';
import { AuthApiService } from '@angular-workspace/services';
import { FormatService } from '../../shared/services/format.service';
import { KeycloakService } from 'keycloak-angular';

export interface ExistingEntityData {
  dataProvider: string;
  type: string;
  username: string;
  status: string;
  createdDate: string;
  action?: string;
  originalData?: any;
}

@Component({
  selector: 'app-admin-existing-entities',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HeaderComponent,
    AdminSidebarComponent,
    PageHeaderComponent,
    TableComponent,
    FilterPanelComponent,
    ModalComponent,
    DataProviderEntityFormComponent,
  ],
  templateUrl: './existing-entities.component.html',
  styleUrl: './existing-entities.component.scss',
})
export class AdminExistingEntitiesComponent implements OnInit, AfterViewInit {
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<any>;
  @ViewChild('viewModalHeaderTemplate', { static: false }) viewModalHeaderTemplate!: TemplateRef<any>;

  constructor(
    private authApiService: AuthApiService,
    private formatService: FormatService,
    private keycloak: KeycloakService,
    private cdr: ChangeDetectorRef
  ) {}

  // Form data for data-provider-entity-form component
  viewFormData: DataProviderEntityFormData = {};

  // Filter fields configuration
  filterFields: FilterField[] = [
    {
      key: 'type',
      label: 'Type',
      type: 'multiselect',
      // Keep labels consistent with Consumer "Create Organisation" entity type dropdown,
      // while sending the backend-required enum value when filtering.
      options: [
        { label: 'Private Limited Company', value: 'PRIVATE_LIMITED' },
        { label: 'Public Limited Company', value: 'PUBLIC_LIMITED' },
        { label: 'Partnership Firm', value: 'PARTNERSHIP' },
        { label: 'Sole Proprietorship', value: 'SOLE_PROPRIETOR' },
        { label: 'Limited Liability Partnership', value: 'LLP' },
        { label: 'Government Organization', value: 'GOVERNMENT' },
        { label: 'Non-Profit Organization', value: 'NON_PROFIT' },
      ],
      value: [],
      maxSelected: 1
    }
  ];

  openDropdownRowIndex: number | null = null;
  currentFilters: { type?: any } = {};
  isViewModalVisible = false;
  selectedEntity: ExistingEntityData | null = null;
  entityDetails: any = null;

  columns: TableColumn[] = [
    {
      key: 'dataProvider',
      label: 'Organisation Name',
      sortable: false,
      width: '25%'
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      width: '20%'
    },
    {
      key: 'username',
      label: 'Username',
      sortable: true,
      width: '20%'
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: '15%'
    },
    {
      key: 'createdDate',
      label: 'Created Date',
      sortable: true,
      width: '12%'
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      width: '8%',
      align: 'left',
      fixed: 'right'
    }
  ];

  allEntitiesData: ExistingEntityData[] = [];
  entitiesData: ExistingEntityData[] = [];

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true
  };

  loading = false;
  emptyMessage = 'No entities found';

  /** Current sort state for server-side sorting */
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  async ngOnInit(): Promise<void> {
    await this.waitForKeycloakReady();
    this.loadEntities();
  }

  private async waitForKeycloakReady(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();
      if (keycloakInstance?.authenticated && keycloakInstance?.token) return;
      const maxWaitTime = 5000, checkInterval = 100, startTime = Date.now();
      while (Date.now() - startTime < maxWaitTime) {
        const instance = this.keycloak.getKeycloakInstance();
        if (instance?.authenticated && instance?.token) return;
        await new Promise(resolve => setTimeout(resolve, checkInterval));
      }
    } catch (error) {
      console.error('Error waiting for Keycloak:', error);
    }
  }

  async loadEntities(): Promise<void> {
    this.loading = true;
    this.openDropdownRowIndex = null;
    this.dropdownPosition = null;
    try {
      // Get entityType from current filters (use first value if array, or the value itself)
      let entityType: string | undefined = undefined;
      if (this.currentFilters.type) {
        if (Array.isArray(this.currentFilters.type) && this.currentFilters.type.length > 0) {
          const first = this.currentFilters.type[0];
          // Options may be strings or {label,value} objects from multiselect
          // Backend filter expects the human-readable label (what the user selects)
          entityType = typeof first === 'string' ? first : (first?.label ?? first?.value);
        } else if (typeof this.currentFilters.type === 'string') {
          entityType = this.currentFilters.type;
        }
      }

      const sortString = this.getSortString();
      const response = await this.authApiService.getOrganisationRequests(
        this.pagination.currentPage,
        this.pagination.pageSize,
        sortString,
        entityType,
        ['granted', 'rejected'] // Only show granted and rejected, exclude pending
      );

      let mappedData: ExistingEntityData[] = [];

      // Handle API response structure: { result: [...], paginationInfo: {...} }
      if (response && response['result'] && Array.isArray(response['result'])) {
        const dataArray = response['result'];
        
        mappedData = dataArray.map((item: any) => {
          // Map API response fields to component data structure
          const dataProvider = item.name || item.organisationName || item.organisation_name || item.orgName || 'N/A';
          const type = item.entityType || item.entity_type || item.type || item.organisationType || 'N/A';
          const username = item.userName || item.user_name || item.username || item.manager || item.managerName || 'N/A';
          
          // Extract and format status
          const status = this.formatService.toPascalCase(
            item.status || item.requestStatus || item.request_status || 'N/A'
          );
          
          // Format date
          let createdDate = 'N/A';
          const requestDate = item.createdAt || item.created_at || item.createdDate || item.created_date;
          if (requestDate) {
            createdDate = this.formatService.formatDate(requestDate);
          }

          return {
            dataProvider,
            type,
            username,
            status,
            createdDate,
            action: 'Action',
            originalData: item // Store original API response data
          };
        });

        // Update pagination from paginationInfo
        if (response['paginationInfo'] && response['paginationInfo']['totalCount'] !== undefined) {
          this.pagination = {
            ...this.pagination,
            totalItems: response['paginationInfo']['totalCount']
          };
        }
      } else if (response && response['paginationInfo']) {
        // Handle case where response has paginationInfo but no result array
        if (response['paginationInfo']['totalCount'] !== undefined) {
          this.pagination = {
            ...this.pagination,
            totalItems: response['paginationInfo']['totalCount']
          };
        }
        mappedData = []; // No data to map
      }

      this.entitiesData = mappedData;
      this.allEntitiesData = [...mappedData];

      if (mappedData.length === 0) {
        this.emptyMessage = 'No entities found';
      }
    } catch (error) {
      console.error('Error loading entities:', error);
      this.entitiesData = [];
      this.allEntitiesData = [];
      this.pagination = {
        ...this.pagination,
        totalItems: 0
      };
      this.emptyMessage = 'No entities found';
    } finally {
      this.loading = false;
    }
  }

  onPageChange(page: number): void {
    this.pagination = {
      ...this.pagination,
      currentPage: page
    };
    this.loadEntities();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = {
      ...this.pagination,
      pageSize: pageSize,
      currentPage: 1
    };
    this.loadEntities();
  }

  ngAfterViewInit(): void {
    // Use withCellTemplates for immutable update - ensures table re-renders with templates (fixes dropdown on first load)
    const updates: { key: string; cellTemplate: TemplateRef<any> }[] = [];
    if (this.actionCellTemplate) updates.push({ key: 'action', cellTemplate: this.actionCellTemplate });
    if (this.statusCellTemplate) updates.push({ key: 'status', cellTemplate: this.statusCellTemplate });
    if (updates.length) {
      this.columns = withCellTemplates(this.columns, updates);
      this.cdr.detectChanges();
    }
  }

  /**
   * Get status chip class based on status value
   */
  getStatusChipClass(status: string): string {
    if (!status) return 'status-chip status-chip-default';
    
    const statusLower = status.toLowerCase();
    if (statusLower === 'granted' || statusLower === 'approved') {
      return 'status-chip status-chip-granted';
    } else if (statusLower === 'rejected') {
      return 'status-chip status-chip-rejected';
    }
    
    return 'status-chip status-chip-default';
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadEntities();
  }

  /**
   * Map UI column key to API sort field string
   */
  getSortString(): string {
    const dir = this.sortColumn ? this.sortDirection : 'desc';
    const apiFieldMap: Record<string, string> = {
      type: 'entityType',
      username: 'userName',
      status: 'status',
      createdDate: 'createdAt',
    };
    const apiField = (this.sortColumn && apiFieldMap[this.sortColumn]) || 'createdAt';
    return `${apiField}:${dir}`;
  }

  toggleActionDropdown(event: Event, row: ExistingEntityData, rowIndex: number): void {
    event.stopPropagation();
    if (this.openDropdownRowIndex === rowIndex) {
      this.openDropdownRowIndex = null;
      this.dropdownPosition = null;
      this.cdr.detectChanges();
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.dropdownPosition = { top: rect.bottom + 4, left: rect.left, width: rect.width };
    this.openDropdownRowIndex = rowIndex;
    this.cdr.detectChanges();
  }

  closeActionDropdown(): void {
    this.openDropdownRowIndex = null;
    this.dropdownPosition = null;
    this.cdr.detectChanges();
  }

  isDropdownOpen(rowIndex: number): boolean {
    return this.openDropdownRowIndex === rowIndex;
  }

  /** Row whose action dropdown is open; used by the fixed overlay. */
  get openDropdownRow(): ExistingEntityData | null {
    if (this.openDropdownRowIndex == null || this.openDropdownRowIndex < 0 || this.openDropdownRowIndex >= this.entitiesData.length) {
      return null;
    }
    return this.entitiesData[this.openDropdownRowIndex] ?? null;
  }

  /** Position for the fixed dropdown overlay. */
  dropdownPosition: { top: number; left: number; width: number } | null = null;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownRowIndex !== null &&
        !target.closest('.action-cell-wrapper') &&
        !target.closest('.action-dropdown-overlay')) {
      this.closeActionDropdown();
    }
  }

  handleAction(action: string, row: ExistingEntityData): void {
    this.closeActionDropdown();
    
    if (action === 'view') {
      // Store the selected entity and open modal
      this.selectedEntity = row;
      this.entityDetails = row.originalData || null;
      
      // Map entity data to form data
      this.mapEntityDataToFormData();
      
      // Open modal
      this.isViewModalVisible = true;
    }
  }

  mapEntityDataToFormData(): void {
    if (!this.entityDetails) {
      console.warn('No entity details available to map form data');
      this.viewFormData = {};
      return;
    }

    // Log the API response structure for debugging
    console.log('Entity Details from API:', this.entityDetails);

    // Helper function to format entity type for display
    const formatEntityType = (value: string): string => {
      if (!value) return '';
      // Convert to PascalCase for display
      return value
        .split(/[_\s-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };

    // Helper function to format entity sector for display
    const formatEntitySector = (value: string): string => {
      if (!value) return '';
      // Convert to PascalCase for display
      return value
        .split(/[_\s-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };

    // Extract user name - could be in various formats
    const userData = this.entityDetails;
    const fullName = userData.userName || 
                     userData.user_name || 
                     userData.name || 
                     userData.fullName ||
                     userData.full_name ||
                     this.selectedEntity?.username ||
                     '';

    const nameParts = fullName.split(' ').filter((part: string) => part.trim());
    const firstName = userData.firstName || 
                      userData.first_name || 
                      (nameParts.length > 0 ? nameParts[0] : '');
    const lastName = userData.lastName || 
                     userData.last_name || 
                     (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

    // Extract document file names and paths
    const documentFiles: { [key: string]: string } = {};
    const documentPaths: { [key: string]: string } = {};
    
    const certPath = userData.certificatePath || userData.certificate_path || '';
    if (certPath) {
      documentFiles['incorporation'] = certPath.split('/').pop() || certPath;
      documentPaths['incorporation'] = certPath;
    }
    
    const panPath = userData.pancardPath || userData.pancard_path || '';
    if (panPath) {
      documentFiles['panCard'] = panPath.split('/').pop() || panPath;
      documentPaths['panCard'] = panPath;
    }
    
    const authPath = userData.orgDocuments || userData.org_documents || userData.letterOfAuthorization || userData.letter_of_authorization || '';
    if (authPath) {
      documentFiles['authorization'] = authPath.split('/').pop() || authPath;
      documentPaths['authorization'] = authPath;
    }
    
    const relPath = userData.relevantDocPath || userData.relevant_doc_path || '';
    if (relPath) {
      documentFiles['others'] = relPath.split('/').pop() || relPath;
      documentPaths['others'] = relPath;
    }

    // Map entity data to form data structure
    this.viewFormData = {
      // User Details
      firstName: firstName,
      lastName: lastName,
      registeredEmail: userData.email || 
                       userData.registeredEmail || 
                       userData.registered_email ||
                       userData.userEmail ||
                       userData.managerEmail ||
                       '',
      jobTitle: userData.jobTitle || 
                userData.job_title || 
                userData.position ||
                '',
      employeeId: userData.empId ||
                  userData.employeeId || 
                  userData.employee_id ||
                  '',
      
      // Entity Details
      entityType: formatEntityType(
        userData.entityType || 
        userData.entity_type || 
        userData.type ||
        userData.organisationType ||
        userData.organisation_type ||
        this.selectedEntity?.type ||
        ''
      ),
      entitySector: formatEntitySector(
        userData.orgSector ||
        userData.entitySector || 
        userData.entity_sector ||
        userData.sector ||
        ''
      ),
      entityName: userData.name || 
                  userData.entityName || 
                  userData.organisationName ||
                  userData.organisation_name ||
                  userData.orgName ||
                  this.selectedEntity?.dataProvider ||
                  '',
      websiteUrl: userData.websiteLink ||
                 userData.websiteUrl || 
                 userData.website_url || 
                 userData.website ||
                 userData.url ||
                 '',
      address: userData.address || 
               userData.fullAddress ||
               userData.full_address ||
               '',
      
      // Document files
      documentFiles: documentFiles,
      // Document paths for PDF viewing
      documentPaths: documentPaths,
    };
  }

  closeViewModal(): void {
    this.isViewModalVisible = false;
    this.selectedEntity = null;
    this.entityDetails = null;
    this.viewFormData = {};
  }

  onFilterChange(filters: Record<string, any>): void {
    // Store current filters
    this.currentFilters = { ...filters };
    
    // Reset to first page when filters change
    this.pagination = {
      ...this.pagination,
      currentPage: 1
    };
    
    // Reload data with new filters
    this.loadEntities();
  }

  onClearFilters(): void {
    // Reset filter fields
    this.filterFields = this.filterFields.map(field => ({
      ...field,
      value: field.type === 'multiselect' ? [] : field.type === 'dateRange' ? null : ''
    }));
    
    // Clear current filters
    this.currentFilters = {};
    
    // Reload data
    this.pagination = {
      ...this.pagination,
      currentPage: 1
    };
    this.loadEntities();
  }

  /**
   * Get modal header with organisation status
   */
  getViewModalHeader(): string {
    if (this.selectedEntity && this.selectedEntity.status) {
      return `Organisation Details - ${this.selectedEntity.status}`;
    }
    return 'Organisation Details';
  }
}

